import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Link2, Unlink, Loader2, Share2 } from "lucide-react";
import { useReportShareToken, useEnsureReportShareToken, useRevokeReportShareToken } from "@/hooks/useReportShareToken";
import { useToast } from "@/hooks/use-toast";

const MESSAGE_TEMPLATE = (link: string) =>
  `Hi! We just finished the pre-arrival cleaning and put together a quick home condition report. If you notice anything that doesn't match when you check in, please let us know right away and we'll take care of it: ${link}`;

interface ShareWithGuestProps {
  cleaningTaskId: string;
  propertyId: string;
  /** Optional: show in a compact single-row style (e.g. inside Reports tab card) */
  compact?: boolean;
}

export function ShareWithGuest({ cleaningTaskId, propertyId, compact }: ShareWithGuestProps) {
  const { toast } = useToast();
  const { data: tokenRow, isLoading } = useReportShareToken(cleaningTaskId);
  const ensureToken = useEnsureReportShareToken();
  const revokeToken = useRevokeReportShareToken();
  const [copied, setCopied] = useState<"message" | "link" | null>(null);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareLink = tokenRow && !tokenRow.revoked_at
    ? (tokenRow.short_code
        ? `${baseUrl}/report/${tokenRow.short_code}`
        : `${baseUrl}/r/${tokenRow.token}`)
    : null;
  const message = shareLink ? MESSAGE_TEMPLATE(shareLink) : "";

  const copyMessage = async () => {
    if (!message) return;
    await navigator.clipboard.writeText(message);
    setCopied("message");
    toast({ title: "Copied message + link" });
    setTimeout(() => setCopied(null), 2000);
  };

  const copyLink = async () => {
    if (!shareLink) return;
    await navigator.clipboard.writeText(shareLink);
    setCopied("link");
    toast({ title: "Copied link" });
    setTimeout(() => setCopied(null), 2000);
  };

  const handleRevoke = async () => {
    if (!tokenRow?.id) return;
    try {
      await revokeToken.mutateAsync(tokenRow.id);
      toast({ title: "Link disabled", description: "The guest report link no longer works." });
    } catch {
      toast({ title: "Could not disable link", variant: "destructive" });
    }
  };

  const handleEnsure = async () => {
    try {
      const result = await ensureToken.mutateAsync(cleaningTaskId);
      const link = result && !result.revoked_at
        ? (result.short_code ? `${baseUrl}/report/${result.short_code}` : `${baseUrl}/r/${result.token}`)
        : null;
      if (link) {
        toast({ title: "Link ready", description: "Share this link with your guest. They’ll see the report with all tasks and photos." });
      } else {
        toast({ title: "Link ready" });
      }
    } catch {
      toast({ title: "Could not create link", variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading…</span>
      </div>
    );
  }

  const hasActiveLink = !!shareLink;

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {!hasActiveLink ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleEnsure}
            disabled={ensureToken.isPending}
          >
            {ensureToken.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4 mr-1.5" />}
            Share with guest
          </Button>
        ) : (
          <>
            <Button type="button" variant="outline" size="sm" onClick={copyMessage}>
              <Copy className="w-4 h-4 mr-1.5" />
              {copied === "message" ? "Copied" : "Copy message + link"}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={copyLink}>
              <Link2 className="w-4 h-4 mr-1.5" />
              {copied === "link" ? "Copied" : "Copy link"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRevoke}
              disabled={revokeToken.isPending}
              className="text-muted-foreground"
            >
              {revokeToken.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4 mr-1.5" />}
              Disable link
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-4 border-t">
      <div className="flex items-center gap-2">
        <Share2 className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium">Share with guest</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Copy a message and link to paste into Airbnb or Hospitable. The guest sees only the report (no address or team details).
      </p>
      {!hasActiveLink ? (
        <Button
          type="button"
          variant="outline"
          className="w-full sm:w-auto"
          onClick={handleEnsure}
          disabled={ensureToken.isPending}
        >
          {ensureToken.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
          Get guest report link
        </Button>
      ) : (
        <>
          <div className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm break-all select-all" title={shareLink ?? undefined}>
            {shareLink}
          </div>
          <p className="text-xs text-muted-foreground">
            Guest opens this link to see the report with all tasks and photos (no address or team details).
          </p>
          <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
            {message}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={copyMessage}>
              <Copy className="w-4 h-4 mr-1.5" />
              {copied === "message" ? "Copied" : "Copy message + link"}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={copyLink}>
              <Link2 className="w-4 h-4 mr-1.5" />
              {copied === "link" ? "Copied" : "Copy link only"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRevoke}
              disabled={revokeToken.isPending}
              className="text-muted-foreground"
            >
              {revokeToken.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Unlink className="w-4 h-4 mr-1.5" />}
              Disable link
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
